import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  TextField,
  IconButton,
  CircularProgress,
  Badge,
  Divider,
  InputAdornment,
  Chip,
} from '@mui/material';
import {
  Send as SendIcon,
  Person as PersonIcon,
  Search as SearchIcon,
  FamilyRestroom as FamilyIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useAuthStore } from '../store/authStore';
import teacherMessageService from '../services/teacherMessage.service';
import parentService, { Parent, ParentMessage } from '../services/parent.service';
import { TeacherConversation, TeacherMessage } from '../types';
import { useNotificationStore } from '../store/notificationStore';

// Unified conversation item for the list
interface ConversationItem {
  kind: 'teacher' | 'parent';
  id: string; // "t-{id}" or "p-{id}"
  name: string;
  lastMessage?: string;
  lastMessageAt?: string;
  unreadCount: number;
  avatarUrl?: string;
  childrenNames?: string;
  // original data
  teacherConv?: TeacherConversation;
  parentData?: Parent;
}

const Messages: React.FC = () => {
  const { user } = useAuthStore();
  const [conversations, setConversations] = useState<TeacherConversation[]>([]);
  const [parents, setParents] = useState<Parent[]>([]);
  const [parentMsgCache, setParentMsgCache] = useState<Record<number, ParentMessage[]>>({});
  const [selectedItem, setSelectedItem] = useState<ConversationItem | null>(null);
  const [messages, setMessages] = useState<TeacherMessage[]>([]);
  const [parentMessages, setParentMessages] = useState<ParentMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const notifications = useNotificationStore((s) => s.notifications);

  // Load conversations + parents
  useEffect(() => {
    loadAll();
  }, []);

  // Refresh parent messages on WS notification
  useEffect(() => {
    const latest = notifications[0];
    if (!latest || latest.category !== 'parent_message') return;
    const parentId = (latest.data as Record<string, unknown>)?.parent_id as number;
    if (!parentId) return;
    // Refresh that parent's messages
    parentService.getMessages(parentId).then((msgs) => {
      setParentMsgCache((prev) => ({ ...prev, [parentId]: msgs }));
      // If currently viewing this parent, update the chat
      if (selectedItem?.kind === 'parent' && selectedItem.parentData?.id === parentId) {
        const sorted = [...msgs].sort((a, b) =>
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        );
        setParentMessages(sorted);
      }
    }).catch(() => {});
  }, [notifications]);

  // Poll for new messages every 10 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      if (selectedItem?.kind === 'teacher' && selectedItem.teacherConv) {
        loadTeacherMessages(selectedItem.teacherConv.id, true);
      }
      if (selectedItem?.kind === 'parent' && selectedItem.parentData) {
        loadParentMessages(selectedItem.parentData.id, true);
      }
      loadAll(true);
    }, 10000);

    return () => clearInterval(interval);
  }, [selectedItem]);

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages, parentMessages]);

  const loadAll = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [convData, parentData] = await Promise.all([
        teacherMessageService.getConversations(),
        parentService.getAll(),
      ]);
      setConversations(convData);
      const parentsList = Array.isArray(parentData) ? parentData : (parentData as any).results || [];
      setParents(parentsList);

      // Load last message for each parent (cache)
      const cache: Record<number, ParentMessage[]> = {};
      await Promise.all(
        parentsList.map(async (p: Parent) => {
          try {
            const msgs = await parentService.getMessages(p.id);
            cache[p.id] = msgs;
          } catch {
            cache[p.id] = [];
          }
        })
      );
      setParentMsgCache(cache);
    } catch (error) {
      console.error('Error loading conversations:', error);
      if (!silent) toast.error('Erreur lors du chargement des conversations');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const loadTeacherMessages = async (conversationId: number, silent = false) => {
    if (!silent) setLoadingMessages(true);
    try {
      const data = await teacherMessageService.getMessages(conversationId);
      setMessages(data);
      // Notify navbar to refresh unread badge (messages are marked as read server-side)
      window.dispatchEvent(new Event('messages-read'));
    } catch (error) {
      console.error('Error loading messages:', error);
      if (!silent) toast.error('Erreur lors du chargement des messages');
    } finally {
      if (!silent) setLoadingMessages(false);
    }
  };

  const loadParentMessages = async (parentId: number, silent = false) => {
    if (!silent) setLoadingMessages(true);
    try {
      const msgs = await parentService.getMessages(parentId);
      const sorted = [...msgs].sort((a, b) =>
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      );
      setParentMessages(sorted);
      setParentMsgCache((prev) => ({ ...prev, [parentId]: msgs }));
    } catch (error) {
      console.error('Error loading parent messages:', error);
      if (!silent) toast.error('Erreur lors du chargement des messages');
    } finally {
      if (!silent) setLoadingMessages(false);
    }
  };

  const handleSelectItem = async (item: ConversationItem) => {
    setSelectedItem(item);
    setMessages([]);
    setParentMessages([]);
    if (item.kind === 'teacher' && item.teacherConv) {
      await loadTeacherMessages(item.teacherConv.id);
      // Refresh conversation list to update badges
      loadAll(true);
    } else if (item.kind === 'parent' && item.parentData) {
      await loadParentMessages(item.parentData.id);
      // Refresh conversation list to update badges (messages marked as read server-side)
      loadAll(true);
      window.dispatchEvent(new Event('messages-read'));
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedItem || !newMessage.trim()) return;

    setSending(true);
    try {
      if (selectedItem.kind === 'teacher' && selectedItem.teacherConv) {
        const message = await teacherMessageService.sendMessage(selectedItem.teacherConv.id, newMessage.trim());
        setMessages(prev => [...prev, message]);
      } else if (selectedItem.kind === 'parent' && selectedItem.parentData) {
        await parentService.sendMessage(selectedItem.parentData.id, newMessage.trim());
        await loadParentMessages(selectedItem.parentData.id, true);
      }
      setNewMessage('');
      loadAll(true);
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error("Erreur lors de l'envoi du message");
    } finally {
      setSending(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();

    if (isToday) {
      return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    }

    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const isYesterday = date.toDateString() === yesterday.toDateString();

    if (isYesterday) {
      return 'Hier ' + date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    }

    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Build unified conversation list
  const buildConversationList = (): ConversationItem[] => {
    const items: ConversationItem[] = [];

    // Teacher conversations
    conversations.forEach((conv) => {
      items.push({
        kind: 'teacher',
        id: `t-${conv.id}`,
        name: `${conv.other_teacher?.first_name || ''} ${conv.other_teacher?.last_name || ''}`.trim(),
        lastMessage: conv.last_message?.content,
        lastMessageAt: conv.last_message?.created_at,
        unreadCount: conv.unread_count,
        avatarUrl: conv.other_teacher?.profile_photo_url || undefined,
        teacherConv: conv,
      });
    });

    // Parent conversations
    parents.forEach((p) => {
      const msgs = parentMsgCache[p.id] || [];
      const lastMsg = msgs.length > 0 ? msgs[msgs.length - 1] : null;
      const unread = msgs.filter((m) => m.sender_type === 'parent' && !m.is_read).length;
      const childrenNames = p.children_details?.map((c) => c.first_name).join(', ');
      items.push({
        kind: 'parent',
        id: `p-${p.id}`,
        name: `${p.first_name} ${p.last_name}`.trim(),
        lastMessage: lastMsg?.content,
        lastMessageAt: lastMsg?.created_at,
        unreadCount: unread,
        childrenNames,
        parentData: p,
      });
    });

    // Sort: conversations with messages first (most recent), then those without
    items.sort((a, b) => {
      if (a.lastMessageAt && b.lastMessageAt) {
        return new Date(b.lastMessageAt).getTime() - new Date(a.lastMessageAt).getTime();
      }
      if (a.lastMessageAt) return -1;
      if (b.lastMessageAt) return 1;
      return a.name.localeCompare(b.name);
    });

    return items;
  };

  const allItems = buildConversationList();
  const filteredItems = allItems.filter((item) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      item.name.toLowerCase().includes(query) ||
      item.childrenNames?.toLowerCase().includes(query)
    );
  });

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        Messages
      </Typography>

      <Paper sx={{ height: 'calc(100vh - 200px)', display: 'flex', overflow: 'hidden' }}>
        {/* Conversations List */}
        <Box sx={{ width: 320, borderRight: 1, borderColor: 'divider', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ p: 2 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Rechercher..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Divider />
          <List sx={{ overflow: 'auto', flex: 1 }}>
            {filteredItems.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  {searchQuery ? 'Aucune conversation trouvée' : 'Aucune conversation'}
                </Typography>
              </Box>
            ) : (
              filteredItems.map((item) => (
                <ListItem
                  key={item.id}
                  button
                  selected={selectedItem?.id === item.id}
                  onClick={() => handleSelectItem(item)}
                  sx={{
                    '&.Mui-selected': {
                      backgroundColor: 'action.selected',
                    },
                  }}
                >
                  <ListItemAvatar>
                    <Badge
                      badgeContent={item.unreadCount}
                      color={item.kind === 'parent' ? 'secondary' : 'primary'}
                      invisible={item.unreadCount === 0}
                    >
                      <Avatar
                        src={item.avatarUrl}
                        sx={item.kind === 'parent' ? { bgcolor: 'secondary.main' } : undefined}
                      >
                        {item.kind === 'parent' ? <FamilyIcon /> : <PersonIcon />}
                      </Avatar>
                    </Badge>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: item.unreadCount > 0 ? 'bold' : 'normal',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {item.name}
                        </Typography>
                        {item.kind === 'parent' && (
                          <Chip label="Parent" size="small" color="secondary" variant="outlined"
                            sx={{ height: 18, fontSize: '0.6rem' }} />
                        )}
                      </Box>
                    }
                    secondary={
                      <>
                        {item.childrenNames && (
                          <Typography variant="caption" color="text.secondary" component="span" sx={{ display: 'block', fontSize: '0.65rem' }}>
                            Parent de {item.childrenNames}
                          </Typography>
                        )}
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          component="span"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            display: 'block',
                            fontWeight: item.unreadCount > 0 ? 'medium' : 'normal',
                          }}
                        >
                          {item.lastMessage || 'Aucun message'}
                        </Typography>
                      </>
                    }
                  />
                  {item.lastMessageAt && (
                    <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', ml: 0.5 }}>
                      {formatTime(item.lastMessageAt)}
                    </Typography>
                  )}
                </ListItem>
              ))
            )}
          </List>
        </Box>

        {/* Messages Area */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          {selectedItem ? (
            <>
              {/* Header */}
              <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center' }}>
                <Avatar
                  src={selectedItem.avatarUrl}
                  sx={selectedItem.kind === 'parent' ? { mr: 2, bgcolor: 'secondary.main' } : { mr: 2 }}
                >
                  {selectedItem.kind === 'parent' ? <FamilyIcon /> : <PersonIcon />}
                </Avatar>
                <Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="subtitle1">
                      {selectedItem.name}
                    </Typography>
                    {selectedItem.kind === 'parent' && (
                      <Chip label="Parent" size="small" color="secondary" sx={{ height: 20, fontSize: '0.7rem' }} />
                    )}
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {selectedItem.kind === 'parent' && selectedItem.childrenNames
                      ? `Parent de ${selectedItem.childrenNames}`
                      : selectedItem.teacherConv?.other_teacher?.email || ''}
                  </Typography>
                </Box>
              </Box>

              {/* Messages */}
              <Box sx={{ flex: 1, overflow: 'auto', p: 2, backgroundColor: 'grey.50' }}>
                {loadingMessages ? (
                  <Box display="flex" justifyContent="center" py={4}>
                    <CircularProgress />
                  </Box>
                ) : selectedItem.kind === 'teacher' ? (
                  /* Teacher messages */
                  messages.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        Aucun message. Commencez la conversation !
                      </Typography>
                    </Box>
                  ) : (
                    messages.map((message) => {
                      const isOwn = message.sender === user?.id;
                      return (
                        <Box
                          key={message.id}
                          sx={{
                            display: 'flex',
                            justifyContent: isOwn ? 'flex-end' : 'flex-start',
                            mb: 1,
                          }}
                        >
                          <Box
                            sx={{
                              maxWidth: '70%',
                              p: 1.5,
                              borderRadius: 2,
                              backgroundColor: isOwn ? 'primary.main' : 'white',
                              color: isOwn ? 'white' : 'text.primary',
                              boxShadow: 1,
                            }}
                          >
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                              {message.content}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                display: 'block',
                                mt: 0.5,
                                textAlign: 'right',
                                opacity: 0.7,
                              }}
                            >
                              {formatTime(message.created_at)}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    })
                  )
                ) : (
                  /* Parent messages */
                  parentMessages.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        Aucun message. Commencez la conversation !
                      </Typography>
                    </Box>
                  ) : (
                    parentMessages.map((msg) => {
                      const isTeacher = msg.sender_type === 'teacher';
                      return (
                        <Box
                          key={msg.id}
                          sx={{
                            display: 'flex',
                            justifyContent: isTeacher ? 'flex-end' : 'flex-start',
                            mb: 1,
                          }}
                        >
                          <Box
                            sx={{
                              maxWidth: '70%',
                              p: 1.5,
                              borderRadius: 2,
                              backgroundColor: isTeacher ? 'primary.main' : 'white',
                              color: isTeacher ? 'white' : 'text.primary',
                              boxShadow: 1,
                            }}
                          >
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                              {msg.content}
                            </Typography>
                            {msg.image && (
                              <Box
                                component="img"
                                src={msg.image}
                                alt=""
                                sx={{ mt: 1, maxWidth: '100%', borderRadius: 1 }}
                              />
                            )}
                            <Typography
                              variant="caption"
                              sx={{
                                display: 'block',
                                mt: 0.5,
                                textAlign: 'right',
                                opacity: 0.7,
                              }}
                            >
                              {formatTime(msg.created_at)}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    })
                  )
                )}
                <div ref={messagesEndRef} />
              </Box>

              {/* Input */}
              <Box
                component="form"
                onSubmit={handleSendMessage}
                sx={{ p: 2, borderTop: 1, borderColor: 'divider', display: 'flex', gap: 1 }}
              >
                <TextField
                  fullWidth
                  placeholder="Écrivez votre message..."
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.ctrlKey && !e.shiftKey) {
                      e.preventDefault();
                      if (newMessage.trim() && !sending) {
                        handleSendMessage(e as any);
                      }
                    } else if (e.key === 'Enter' && e.ctrlKey) {
                      e.preventDefault();
                      setNewMessage(prev => prev + '\n');
                    }
                  }}
                  variant="outlined"
                  size="small"
                  multiline
                  maxRows={4}
                  disabled={sending}
                />
                <IconButton
                  type="submit"
                  color="primary"
                  disabled={!newMessage.trim() || sending}
                >
                  {sending ? <CircularProgress size={24} /> : <SendIcon />}
                </IconButton>
              </Box>
            </>
          ) : (
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'grey.50',
              }}
            >
              <Box sx={{ textAlign: 'center' }}>
                <PersonIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  Sélectionnez une conversation
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Choisissez un contact dans la liste pour commencer
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default Messages;
